import React, { useRef, useEffect, useState } from 'react';
import { IconButton, Slider, Avatar } from '@material-ui/core';
import { PlayArrow, Pause } from '@material-ui/icons';
import styled from 'styled-components';
import { fetchAuthenticatedMedia } from '../../services/mediaLibraryService';
import AuthenticatedImage from '../AuthenticatedImage';

const LS_NAME = 'audioMessageRate';

// Só o player (avatar + play + barra + tempo). O card é o bubble da mensagem no pai (Atendimentos / MessagesList).
const PlayerContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !String(prop).startsWith('$'),
})`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  width: 100%;
  max-width: 300px;

  ${(p) =>
    p.$bubbleTone === 'outgoing'
      ? `
    background: rgba(255, 255, 255, 0.94);
    border-radius: 18px;
    padding: 8px 12px 10px;
    box-shadow: 0 1px 1px rgba(11, 20, 26, 0.1);
  `
      : ''}
  ${(p) =>
    p.$bubbleTone === 'incoming'
      ? `
    background: rgba(0, 0, 0, 0.045);
    border-radius: 18px;
    padding: 8px 12px 10px;
  `
      : ''}
`;

// Avatar do usuário
const UserAvatar = styled(Avatar)`
  width: 32px;
  height: 32px;
`;

// Botão de play/pause estilizado
const PlayPauseButton = styled(IconButton)`
  background-color: transparent;
  padding: 4px;
  color: #00a884;

  &:hover {
    background-color: rgba(0, 168, 132, 0.1);
  }

  .MuiSvgIcon-root {
    font-size: 28px;
  }
`;

// Container da barra de progresso e tempo
const ProgressContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

// Barra de progresso estilizada
const ProgressBar = styled(Slider)`
  color: #00a884;
  padding: 0 !important;
  height: 4px;

  .MuiSlider-thumb {
    width: 10px;
    height: 10px;
    background-color: #00a884;
    top: 50%;
    transform: translateY(-50%);
    margin-top: 0;
    box-shadow: none;

    &:hover,
    &.Mui-focusVisible {
      box-shadow: 0 0 0 6px rgba(0, 168, 132, 0.2);
    }
  }

  .MuiSlider-track {
    background-color: #00a884;
    height: 4px;
    border-radius: 2px;
  }

  .MuiSlider-rail {
    background-color: rgba(0, 168, 132, 0.2);
    height: 4px;
    border-radius: 2px;
  }
`;

// Exibição do tempo
const TimeDisplay = styled.div`
  font-size: 11px;
  color: #667781;
  font-weight: 400;
  text-align: right;
`;

// Função para formatar o tempo em mm:ss
const formatTime = (time) => {
  if (time == null || !isFinite(time) || isNaN(time)) return '--:--';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
};

/** Atendimento/WhatsApp: só url, avatarUrl, userName. bubbleTone + hideAvatar = só chat interno (não altera tickets). */
const AudioModal = ({ url, avatarUrl, userName, bubbleTone, hideAvatar }) => {
  const audioRef = useRef(null);
  const [audioRate, setAudioRate] = useState(parseFloat(localStorage.getItem(LS_NAME) || '1'));
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [_duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const [_blobUrl, setBlobUrl] = useState('');
  const [fetchError, setFetchError] = useState(false);
  // Duração real capturada após primeira reprodução completa (WebM/Opus não tem duration no header)
  const [knownDuration, setKnownDuration] = useState(0);

  // Retorna o path relativo para api.get() (sempre com auth)
  const getApiPath = (inputUrl) => {
    if (typeof inputUrl !== 'string') return '';
    if (inputUrl.startsWith('/')) return inputUrl;
    try {
      const u = new URL(inputUrl);
      return u.pathname + u.search;
    } catch {
      return inputUrl;
    }
  };

  // Atualiza a taxa de reprodução no localStorage
  useEffect(() => {
    audioRef.current.playbackRate = audioRate;
    localStorage.setItem(LS_NAME, audioRate);
  }, [audioRate]);

  // Atualiza o tempo atual e a duração do áudio
  useEffect(() => {
    const audio = audioRef.current;

    const updateTime = () => setCurrentTime(audio.currentTime);

    const updateDuration = () => {
      if (isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
        setKnownDuration(audio.duration);
      }
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    const handleEnded = () => {
      setIsPlaying(false);
      // Sempre captura currentTime como duração real ao terminar —
      // browsers resolvem audio.duration do WebM só após decodificar tudo.
      if (audio.currentTime > 0) {
        setDuration(audio.currentTime);
        setKnownDuration(audio.currentTime);
      }
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('durationchange', updateDuration);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('durationchange', updateDuration);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  // Para URLs protegidas por Bearer, <audio src> não envia Authorization.
  // Usamos api.get() com path relativo para garantir que o token seja enviado.
  // O src é setado diretamente no elemento para evitar que load() seja chamado
  // antes do DOM ser atualizado pelo React (o que causava estalos/artefatos).
  useEffect(() => {
    let cancelled = false;
    let createdObjectUrl = '';

    const run = async () => {
      const apiPath = getApiPath(url);
      if (!apiPath) {
        setFetchError(true);
        return;
      }

      const pathToFetch = isIOS ? apiPath.replace('.ogg', '.mp3') : apiPath;
      setFetchError(false);

      try {
        const { data, headers } = await fetchAuthenticatedMedia(pathToFetch, 'blob');
        if (cancelled) return;
        // Áudio WhatsApp é Opus em container OGG; especificar codec evita
        // que o browser tente decodar como Vorbis e produza artefatos.
        const type = headers?.['content-type'] || (isIOS ? 'audio/mpeg' : 'audio/ogg; codecs=opus');
        createdObjectUrl = URL.createObjectURL(new Blob([data], { type }));
        if (audioRef.current) {
          audioRef.current.src = createdObjectUrl;
          audioRef.current.load();
        }
        setBlobUrl(createdObjectUrl);
      } catch (_err) {
        if (cancelled) return;
        setFetchError(true);
      }
    };

    run();

    return () => {
      cancelled = true;
      if (createdObjectUrl) URL.revokeObjectURL(createdObjectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, isIOS]);

  // Alternar entre play e pause
  const togglePlayPause = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
  };

  // Alternar a velocidade de reprodução
  const _toggleRate = () => {
    const rates = [0.5, 1, 1.5, 2];
    const currentIndex = rates.indexOf(audioRate);
    const newRate = rates[(currentIndex + 1) % rates.length];
    setAudioRate(newRate);
  };

  // Atualizar o volume
  const _handleVolumeChange = (event, newValue) => {
    setVolume(newValue);
    audioRef.current.volume = newValue;
    setIsMuted(newValue === 0);
  };

  // Alternar entre mudo e desmudo
  const _toggleMute = () => {
    if (isMuted) {
      audioRef.current.volume = volume;
    } else {
      audioRef.current.volume = 0;
    }
    setIsMuted(!isMuted);
  };

  // Atualizar o tempo do áudio ao arrastar a barra de progresso
  const handleSeek = (event, newValue) => {
    audioRef.current.currentTime = newValue;
    setCurrentTime(newValue);
  };

  // Qualquer URL que não seja http(s) absoluta exige carregamento com Bearer (api.get) → usar AuthenticatedImage
  const isAbsoluteExternalUrl =
    typeof avatarUrl === 'string' &&
    (avatarUrl.startsWith('http://') || avatarUrl.startsWith('https://'));
  const avatarNeedsAuth = avatarUrl && typeof avatarUrl === 'string' && !isAbsoluteExternalUrl;

  const playBtnStyle = bubbleTone === 'outgoing' ? { color: '#111b21' } : undefined;
  const timeStyle = bubbleTone ? { color: '#667781' } : undefined;

  return (
    <PlayerContainer $bubbleTone={bubbleTone}>
      {!hideAvatar &&
        (avatarNeedsAuth ? (
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            <AuthenticatedImage
              imageUrl={avatarUrl}
              alt={userName}
              style={{ width: 32, height: 32, minWidth: 32, minHeight: 32, objectFit: 'cover' }}
            />
          </div>
        ) : (
          <UserAvatar src={avatarUrl} alt={userName}>
            {!avatarUrl && userName?.charAt(0).toUpperCase()}
          </UserAvatar>
        ))}
      <PlayPauseButton style={playBtnStyle} onClick={togglePlayPause}>
        {isPlaying ? <Pause /> : <PlayArrow />}
      </PlayPauseButton>
      <ProgressContainer>
        <ProgressBar
          value={knownDuration > 0 ? currentTime : 0}
          max={knownDuration > 0 ? knownDuration : 1}
          disabled={knownDuration === 0}
          onChange={handleSeek}
          aria-labelledby="audio-seek-slider"
        />
        <TimeDisplay style={timeStyle}>
          {fetchError
            ? 'Erro ao carregar áudio'
            : knownDuration > 0
              ? `${formatTime(currentTime)} / ${formatTime(knownDuration)}`
              : formatTime(currentTime)}
        </TimeDisplay>
      </ProgressContainer>
      <audio ref={audioRef} style={{ display: 'none' }} />
    </PlayerContainer>
  );
};

export default AudioModal;
